import React, { useState, useEffect, useRef } from 'react';
import {
  Search, Command, Bell, Sun, Moon, ChevronDown, ChevronRight, CornerDownLeft, ArrowLeft,
  Plus, Camera, Upload, FileDown, FileText, BookOpen, Package, DoorOpen, LayoutGrid, Truck,
  DollarSign, BarChart3, Users, Wrench, Zap, Calendar as CalIcon, ListTodo, ListChecks,
  Sofa, Palette, Footprints, Paintbrush, ClipboardList, FlaskConical, Ruler,
  Image as ImageIcon, Check, Store, Bot, Star, Layers, Home,
} from 'lucide-react';

const FONT = "'Jost', 'Century Gothic', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif";
const LOGO = '/established-logo.png';
const APPICON = '/icon-192x192.png';

/* Palette pulled from the live app: deep navy→black with GOLD/TAN accents (no blue) */
const THEMES = {
  dark: {
    page: '#0F0F0F',
    rail: 'linear-gradient(180deg, #1A1A1A 0%, #0A0A0A 100%)',
    topbar: 'linear-gradient(90deg, #1E1E1E 0%, #141414 60%, #0F0F0F 100%)',
    goldGrad: 'linear-gradient(90deg, #C9A05E 0%, #E7C98C 50%, #C9A05E 100%)',
    card: '#1A1A1A', cardSubtle: '#262626',
    border: 'rgba(212,165,116,0.18)', borderStrong: 'rgba(212,165,116,0.5)',
    text: '#E0E0E0', text2: '#A8A8A8', muted: '#7A7A7A',
    gold: '#D4A574', goldBright: '#E7C98C', brown: '#A67C52', grey: '#9AA0A6',
    success: '#4FA07A', warning: '#D08A4A', purple: '#8877AE', teal: '#4E9E90', danger: '#C0574E',
    shadow: '0 4px 22px rgba(0,0,0,0.5)', shadowHover: '0 12px 36px rgba(0,0,0,0.65)',
    chip: 'rgba(212,165,116,0.10)', chipBorder: 'rgba(212,165,116,0.22)',
    onGold: '#1A1206',
  },
  light: {
    page: 'linear-gradient(180deg, #F3F0E9 0%, #FBF9F4 100%)',
    rail: 'linear-gradient(180deg, #141A28 0%, #06080D 100%)',
    topbar: 'linear-gradient(90deg, #141A28 0%, #0B0E15 60%, #06080D 100%)',
    goldGrad: 'linear-gradient(90deg, #C9A05E 0%, #E7C98C 50%, #C9A05E 100%)',
    card: '#FFFFFF', cardSubtle: '#F4F1EA',
    border: 'rgba(139,115,85,0.38)', borderStrong: 'rgba(139,115,85,0.75)',
    text: '#1A1611', text2: '#3A332A', muted: '#5F584C',
    gold: '#B8894C', goldBright: '#C9A05E', brown: '#8B7355', grey: '#6B7280',
    success: '#3E8C68', warning: '#C07A38', purple: '#6F5F97', teal: '#3E8A7C', danger: '#B24A42',
    shadow: '0 2px 12px rgba(60,45,25,0.08)', shadowHover: '0 12px 30px rgba(60,45,25,0.16)',
    chip: 'rgba(255,255,255,0.10)', chipBorder: 'rgba(255,255,255,0.18)',
    onGold: '#1A1206',
  },
};

const RAIL = [
  { key: 'home', label: 'Home', icon: Home },
  { key: 'projects', label: 'Projects', icon: Layers },
  { key: 'calendar', label: 'Calendar', icon: CalIcon },
  { key: 'logistics', label: 'Logistics', icon: Truck },
  { key: 'financial', label: 'Financial', icon: DollarSign },
  { key: 'reports', label: 'Reports', icon: BarChart3 },
  { key: 'people', label: 'People', icon: Users },
  { key: 'tools', label: 'Tools', icon: Wrench },
];

/* The REAL top project menu (matches the live app), redesigned clean */
const PRIMARY = [
  { label: 'Questionnaire', icon: ClipboardList },
  { label: 'Walkthrough', icon: Footprints },
  { label: 'Checklist', icon: ListChecks },
  { label: 'FF&E', icon: Sofa },
  { label: 'Whole Home Finishes', icon: Palette },
  { label: 'Room Finishes', icon: Paintbrush },
];
const SECONDARY = [
  { label: 'Tasks', drop: true }, { label: 'Calendar' }, { label: 'Design' },
  { label: 'Design Tools', drop: true }, { label: 'Logistics', drop: true },
  { label: 'Financial', drop: true }, { label: 'Automation' }, { label: 'Exports' },
  { label: 'Reports', drop: true }, { label: 'People', drop: true },
  { label: 'Samples' }, { label: 'Measurements' },
];

/* Menu: big pinned favorites (most-used) + everything else grouped by workflow phase */
const PINNED = [
  { label: 'Walkthrough', icon: Footprints },
  { label: 'Checklist', icon: ListChecks },
  { label: 'FF&E', icon: Sofa },
  { label: 'Whole Home Finishes', icon: Palette },
  { label: 'Tasks', icon: ListTodo },
  { label: 'Trade Sheets', icon: FileText },
];
const PHASES = [
  { label: 'Discovery', items: ['Questionnaire', 'Measurements'] },
  { label: 'Selections', items: ['Room Finishes', 'Samples'] },
  { label: 'Design', items: ['Design', 'Design Tools'] },
  { label: 'Manage', items: ['Calendar', 'Automation'] },
  { label: 'Procurement', items: ['Financial', 'Logistics'] },
  { label: 'Output', items: ['Reports', 'Exports'] },
  { label: 'Team', items: ['People'] },
];
const ICONMAP = {
  'Questionnaire': ClipboardList, 'Measurements': Ruler, 'Whole Home Finishes': Palette,
  'Room Finishes': Paintbrush, 'Samples': FlaskConical, 'Design': ImageIcon, 'Design Tools': Wrench,
  'Calendar': CalIcon, 'Automation': Zap, 'Financial': DollarSign, 'Logistics': Truck,
  'Reports': BarChart3, 'Exports': FileDown, 'People': Users,
};
const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, '-');

const COVERS = [
  'https://images.unsplash.com/photo-1648881806148-e5c51179c826?crop=entropy&cs=srgb&fm=jpg&w=600&q=70&ixlib=rb-4.1.0',
  'https://images.unsplash.com/photo-1564078516393-cf04bd966897?crop=entropy&cs=srgb&fm=jpg&w=600&q=70&ixlib=rb-4.1.0',
  'https://images.unsplash.com/photo-1611048267451-e6ed903d4a38?crop=entropy&cs=srgb&fm=jpg&w=600&q=70&ixlib=rb-4.1.0',
];
const PROJECTS = [
  { name: 'Diehl Lakehouse', client: 'Liz Diehl', addr: '6349 J F Jay Rd', accent: 'gold', rooms: 14, pct: 68 },
  { name: 'Wheeler Ridge', client: 'M. Wheeler', addr: '221 Ridge Line', accent: 'brown', rooms: 9, pct: 42 },
  { name: 'Stutzman House', client: 'Debbie Stutzman', addr: '88 Lakeview Dr', accent: 'goldBright', rooms: 11, pct: 55 },
];

/* Quick actions = OUR app's real features */
const ACTIONS = [
  { label: 'Import Houzz PDF', icon: Upload }, { label: 'Add Item', icon: Plus },
  { label: 'Add Room', icon: DoorOpen }, { label: 'Export PDF Report', icon: FileDown },
  { label: 'Room Finish Schedules', icon: LayoutGrid }, { label: 'Photo Management', icon: ImageIcon },
  { label: 'Shipping Tracker', icon: Truck }, { label: 'Brand Catalogs', icon: BookOpen },
  { label: 'My Items', icon: Star },
];

const STATUS = [
  { label: 'Approved', n: 184, tone: 'success' },
  { label: 'To Be Selected', n: 95, tone: 'grey' },
  { label: 'Ordered', n: 21, tone: 'warning' },
  { label: 'In Production', n: 13, tone: 'brown' },
  { label: 'In Transit', n: 5, tone: 'purple' },
  { label: 'Received', n: 3, tone: 'teal' },
  { label: 'Shipped', n: 1, tone: 'gold' },
];

const TASKS = [
  { t: 'Approve Diehl FF&E swatches', done: false },
  { t: 'Send Wheeler proposal v2', done: false },
  { t: 'Confirm Four Hands delivery', done: true },
  { t: 'Order concrete side table', done: false },
  { t: 'Schedule install — master bath', done: false },
];

const ALL_ITEMS = [
  ...PRIMARY.map(p => ({ ...p, group: 'Sheets' })),
  { label: 'Tasks', icon: ListTodo, group: 'Planning' },
  { label: 'Calendar', icon: CalIcon, group: 'Planning' },
  { label: 'Design', icon: ImageIcon, group: 'Planning' },
  { label: 'Automation', icon: Zap, group: 'Planning' },
  { label: 'Logistics', icon: Truck, group: 'Logistics' },
  { label: 'Financial', icon: DollarSign, group: 'Financial' },
  { label: 'Reports', icon: BarChart3, group: 'Reports' },
  { label: 'AI Assistant', icon: Bot, group: 'Reports' },
  { label: 'People', icon: Users, group: 'People' },
  { label: 'Vendors', icon: Store, group: 'People' },
  { label: 'Samples', icon: FlaskConical, group: 'Tools' },
  { label: 'Measurements', icon: Ruler, group: 'Tools' },
];

const CATS = {
  shipping: { label: 'Shipping', c: '#4A7FB5' }, delivery: { label: 'Delivery', c: '#4FA07A' },
  install: { label: 'Install', c: '#C0574E' }, order: { label: 'Order', c: '#D08A4A' },
  restock: { label: 'Restock', c: '#8877AE' }, project: { label: 'Project', c: '#A67C52' },
};
const EVENTS = {
  4: [{ l: 'Install — Bath', c: 'install' }],
  9: [{ l: 'Anderson Delivery', c: 'shipping' }],
  10: [{ l: 'Order — Four Hands', c: 'order' }],
  12: [{ l: 'Restock linens', c: 'restock' }],
  16: [{ l: 'Established Design', c: 'project' }],
  17: [{ l: 'Aven Birthday', c: 'delivery' }],
  18: [{ l: 'Pay Day', c: 'shipping' }],
  23: [{ l: 'Order — Rugs', c: 'order' }],
  24: [{ l: 'Delivery — Rowe', c: 'delivery' }],
  27: [{ l: 'Install — Kitchen', c: 'install' }],
};
const TODO_GROUPS = [
  { name: 'Wheeler Ridge', count: 32, tone: 'brown', tasks: [
    { t: 'ASK CHARLENE: Wall Color (Living)', p: 'high' },
    { t: 'ASK NEIL: Wall Color (Living Rm)', p: 'high' },
    { t: 'Switch out chairs in Primary Bath', p: 'medium', d: '2/2/2026' },
  ]},
  { name: 'Emileigh Greene', count: 2, tone: 'success', tasks: [
    { t: 'Order Wallpaper', p: 'urgent', d: '2/9/2026' },
    { t: 'Order Wallpaper', p: 'urgent', d: '2/9/2026' },
  ]},
  { name: "The Elliott's", count: 4, tone: 'gold', tasks: [
    { t: 'ORDER SAMPLES: Pamesa Tresana', p: 'medium' },
    { t: 'ASK NEIL: Lustrous 3-Lt Sconce', p: 'high' },
    { t: 'ASK NEIL: Elsher Mirror (Primary)', p: 'high' },
  ]},
  { name: 'Tonia Anderson', count: 8, tone: 'purple', tasks: [
    { t: 'ORDER SAMPLES: Sloane Bed', p: 'medium' },
    { t: 'ORDER SAMPLES: CASEN CSN01', p: 'medium' },
    { t: 'ORDER SAMPLES: HAZAN HAZ02', p: 'medium' },
  ]},
];
const PRIO = { urgent: 'danger', high: 'warning', medium: 'grey' };

/* ---- blocks ---- */
const Card = ({ T, accent, children, className = '', style = {}, ...rest }) => {
  const [h, setH] = useState(false);
  const ac = accent ? T[accent] : null;
  return (
    <div {...rest} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      className={`rounded-xl overflow-hidden ${className}`}
      style={{
        background: T.card, border: `1px solid ${T.border}`,
        borderTop: ac ? `3px solid ${ac}` : `1px solid ${T.border}`,
        boxShadow: h ? T.shadowHover : T.shadow,
        transform: h ? 'translateY(-3px)' : 'none',
        transition: 'transform .2s ease, box-shadow .2s ease, background .3s, border-color .3s', ...style,
      }}>{children}</div>
  );
};
const Badge = ({ T, tone = 'gold', children }) => (
  <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium" style={{ background: `${T[tone]}22`, color: T[tone] }}>
    <span className="h-1.5 w-1.5 rounded-full" style={{ background: T[tone] }} />{children}
  </span>
);
const Overline = ({ T, children }) => (
  <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: T.muted }}>{children}</div>
);

const CommandPalette = ({ open, onClose, T }) => {
  const [q, setQ] = useState(''); const ref = useRef(null);
  useEffect(() => { if (open && ref.current) ref.current.focus(); if (!open) setQ(''); }, [open]);
  if (!open) return null;
  const res = ALL_ITEMS.filter(i => i.label.toLowerCase().includes(q.toLowerCase())).slice(0, 7);
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center pt-28" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)' }} onClick={onClose} data-testid="cmdk-overlay">
      <div className="w-[460px] overflow-hidden rounded-2xl" style={{ background: T.card, border: `1px solid ${T.borderStrong}`, boxShadow: T.shadowHover }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${T.border}` }}>
          <Search size={15} style={{ color: T.gold }} />
          <input ref={ref} value={q} onChange={e => setQ(e.target.value)} placeholder="Jump to a section or run a command…" data-testid="cmdk-input" className="flex-1 bg-transparent text-sm outline-none" style={{ color: T.text, fontFamily: FONT }} />
          <span className="rounded px-1.5 py-0.5 text-[10px]" style={{ background: T.cardSubtle, color: T.muted }}>ESC</span>
        </div>
        <div className="max-h-72 overflow-y-auto py-1">
          {res.map((r, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-2.5 text-sm cursor-pointer" style={{ color: T.text }}
              onMouseEnter={e => e.currentTarget.style.background = T.cardSubtle} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <r.icon size={15} style={{ color: T.gold }} /><span className="flex-1">{r.label}</span>
              <span className="text-[10px]" style={{ color: T.muted }}>{r.group}</span><CornerDownLeft size={12} style={{ color: T.muted }} />
            </div>
          ))}
          {res.length === 0 && <div className="px-4 py-6 text-center text-xs" style={{ color: T.muted }}>No matches</div>}
        </div>
      </div>
    </div>
  );
};

/* ================= MAIN ================= */
const LayoutOptionsPreview = () => {
  const [mode, setMode] = useState('light');
  const [activeRail, setActiveRail] = useState('home');
  const [activeMenu, setActiveMenu] = useState('FF&E');
  const [cmd, setCmd] = useState(false);
  const [view, setView] = useState('dashboard');
  const DEMO_PID = 'a0000000-0000-4000-8000-000000000002';
  const T = THEMES[mode];

  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setCmd(true); }
      if (e.key === 'Escape') setCmd(false);
    };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, []);
  const total = STATUS.reduce((s, x) => s + x.n, 0);
  // build conic gradient for donut
  let acc = 0; const stops = STATUS.map(s => { const from = acc / total * 360; acc += s.n; const to = acc / total * 360; return `${T[s.tone]} ${from}deg ${to}deg`; }).join(', ');

  return (
    <div id="est-layout-preview" style={{ fontFamily: FONT, minHeight: '100vh', background: T.page, color: T.text, transition: 'background .35s, color .35s' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Jost:wght@300;400;500;600;700&display=swap');
        #est-layout-preview button { background-color: transparent; border: 1px solid transparent; color: inherit; }
        #est-layout-preview button:hover { background-color: transparent; }
        #est-layout-preview input { background-color: transparent !important; border: none !important; color: inherit !important; }
      `}</style>
      <div className="flex items-center justify-center gap-2 px-4 py-1.5 text-[11px]" style={{ background: T.chip, color: T.text2, borderBottom: `1px solid ${T.border}` }}>
        <span className="font-semibold" style={{ color: T.gold }}>PREVIEW</span>
        <span>Concept v3 · gold theme · nothing in your live app changes · try</span>
        <span className="rounded px-1.5 py-0.5" style={{ background: T.cardSubtle, color: T.text }}>⌘K</span>
      </div>

      <div className="flex">
        {/* icon rail */}
        <aside className="sticky top-0 flex h-screen w-[68px] flex-col items-center py-4" style={{ background: T.rail }}>
          <img src={APPICON} alt="Established Design Co." className="h-9 w-9 rounded-lg object-cover" />
          <div className="mt-6 flex flex-1 flex-col items-center gap-1.5">
            {RAIL.map(r => {
              const on = activeRail === r.key;
              return (
                <button key={r.key} title={r.label} data-testid={`rail-${r.key}`} onClick={() => setActiveRail(r.key)}
                  className="grid h-11 w-11 place-items-center rounded-xl transition-all"
                  style={{ background: on ? T.goldGrad : 'transparent', color: on ? T.onGold : 'rgba(241,234,219,0.6)' }}
                  onMouseEnter={e => { if (!on) e.currentTarget.style.background = 'rgba(212,165,116,0.14)'; }}
                  onMouseLeave={e => { if (!on) e.currentTarget.style.background = 'transparent'; }}>
                  <r.icon size={19} />
                </button>
              );
            })}
          </div>
          <button data-testid="rail-cmdk" onClick={() => setCmd(true)} title="Command (⌘K)" className="grid h-11 w-11 place-items-center rounded-xl" style={{ color: 'rgba(241,234,219,0.6)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(212,165,116,0.14)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <Command size={18} />
          </button>
          <div className="mt-2 grid h-9 w-9 place-items-center rounded-full text-xs font-bold" style={{ background: T.goldGrad, color: T.onGold }}>KD</div>
        </aside>

        <main className="min-w-0 flex-1">
          {/* top utility bar */}
          <header className="sticky top-0 z-40 flex items-center gap-4 px-5" style={{ height: 62, background: T.topbar, borderBottom: `1px solid ${T.border}` }}>
            <div aria-label="Established Design Co." style={{
              height: 40, width: 168,
              background: T.goldGrad,
              WebkitMaskImage: `url(${LOGO})`, maskImage: `url(${LOGO})`,
              WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
              WebkitMaskSize: 'contain', maskSize: 'contain',
              WebkitMaskPosition: 'left center', maskPosition: 'left center',
            }} />
            <button data-testid="project-switcher" className="ml-2 flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors" style={{ background: T.chip, border: `1px solid ${T.chipBorder}`, color: T.text }}>
              <span className="h-3.5 w-3.5 rounded" style={{ background: T.gold }} /> Diehl Lakehouse <ChevronDown size={13} style={{ color: T.gold }} />
            </button>
            <div className="mx-auto hidden max-w-md flex-1 sm:block">
              <button data-testid="global-search-input" onClick={() => setCmd(true)} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs transition-colors" style={{ background: T.chip, border: `1px solid ${T.chipBorder}`, color: T.text2 }}>
                <Search size={14} /><span>Search projects, items, vendors…</span>
                <span className="ml-auto flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px]" style={{ background: 'rgba(212,165,116,0.2)', color: T.goldBright }}><Command size={9} />K</span>
              </button>
            </div>
            <button className="relative grid h-9 w-9 place-items-center rounded-lg transition-colors" style={{ color: T.text2 }}>
              <Bell size={17} /><span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full" style={{ background: T.gold }} />
            </button>
            <button data-testid="theme-toggle-button" onClick={() => setMode(m => m === 'dark' ? 'light' : 'dark')} className="flex items-center gap-1 rounded-full p-1" style={{ background: 'rgba(212,165,116,0.14)', border: `1px solid ${T.chipBorder}` }}>
              <span className="grid h-6 w-6 place-items-center rounded-full" style={{ background: mode === 'light' ? T.goldGrad : 'transparent', color: mode === 'light' ? T.onGold : 'rgba(241,234,219,0.55)' }}><Sun size={13} /></span>
              <span className="grid h-6 w-6 place-items-center rounded-full" style={{ background: mode === 'dark' ? T.goldGrad : 'transparent', color: mode === 'dark' ? T.onGold : 'rgba(241,234,219,0.55)' }}><Moon size={13} /></span>
            </button>
            <div className="grid h-9 w-9 place-items-center rounded-full text-xs font-bold" style={{ background: T.goldGrad, color: T.onGold }}>KD</div>
          </header>

          {/* project title + export (matches live app) */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-6 pt-5">
            <div>
              <button className="mb-1 flex items-center gap-1.5 text-xs" style={{ color: T.text2 }}><ArrowLeft size={13} /> Back to All Projects</button>
              <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl" style={{ color: T.text }}>Diehl Lakehouse</h1>
              <div className="mt-1 text-lg font-semibold" style={{ color: T.gold }}>Liz Diehl</div>
              <div className="text-sm" style={{ color: T.text2 }}>6349 J F Jay Rd</div>
            </div>
            <button className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold" style={{ background: T.goldGrad, color: T.onGold }}>
              <FileDown size={16} /> Export PDF Report
            </button>
          </div>

          {/* PROJECT MENU — pinned favorites + grouped by workflow phase */}
          <div className="mx-6 mt-4 rounded-xl p-4" style={{ background: T.card, border: `1.5px solid ${T.borderStrong}`, boxShadow: T.shadow }}>
            <div className="mb-2">
              <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: T.muted }}>Pinned</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {PINNED.map(m => {
                const on = activeMenu === m.label;
                return (
                  <button key={m.label} data-testid={`menu-${slug(m.label)}`} onClick={() => { setActiveMenu(m.label); setView(m.label === 'FF&E' ? 'ffe' : m.label === 'Checklist' ? 'checklist' : 'dashboard'); }}
                    className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all"
                    style={on ? { background: T.goldGrad, color: T.onGold, boxShadow: T.shadow } : { background: T.cardSubtle, color: T.text, border: `1px solid ${T.border}` }}
                    onMouseEnter={e => { if (!on) e.currentTarget.style.borderColor = T.gold; }}
                    onMouseLeave={e => { if (!on) e.currentTarget.style.borderColor = T.border; }}>
                    <m.icon size={16} /> {m.label}
                  </button>
                );
              })}
            </div>
            <div className="my-3 h-px w-full" style={{ background: T.border }} />
            <div className="space-y-2">
              {PHASES.map(ph => (
                <div key={ph.label} className="flex flex-wrap items-center gap-2">
                  <span className="w-24 shrink-0 text-[10px] font-bold uppercase tracking-widest" style={{ color: T.gold }}>{ph.label}</span>
                  {ph.items.map(label => {
                    const Ic = ICONMAP[label] || Layers;
                    const on = activeMenu === label;
                    return (
                      <button key={label} data-testid={`menu-${slug(label)}`} onClick={() => setActiveMenu(label)}
                        className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-medium transition-all"
                        style={on ? { background: T.gold, color: T.onGold, border: `1px solid ${T.gold}` } : { background: T.cardSubtle, color: T.text, border: `1px solid ${T.border}` }}
                        onMouseEnter={e => { if (!on) e.currentTarget.style.borderColor = T.gold; }}
                        onMouseLeave={e => { if (!on) e.currentTarget.style.borderColor = T.border; }}>
                        <Ic size={14} style={{ color: on ? T.onGold : T.gold }} /> {label}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* dashboard grid */}
          {/* content view switch: dashboard vs a real, live sheet embedded in the new shell */}
          <div className="mx-6 mt-5 flex flex-wrap items-center gap-2">
            {[['dashboard', 'Dashboard'], ['ffe', 'FF&E Sheet'], ['checklist', 'Checklist']].map(([v, label]) => {
              const on = view === v;
              return (
                <button key={v} data-testid={`view-${v}`} onClick={() => { setView(v); if (v === 'ffe') setActiveMenu('FF&E'); if (v === 'checklist') setActiveMenu('Checklist'); }}
                  className="rounded-lg px-4 py-2 text-[13px] font-semibold transition-all"
                  style={on ? { background: T.goldGrad, color: T.onGold } : { background: T.cardSubtle, color: T.text, border: `1px solid ${T.border}` }}>
                  {label}
                </button>
              );
            })}
            {view !== 'dashboard' && <span className="text-xs" style={{ color: T.muted }}>Your real, live sheet — unchanged — shown inside the new shell</span>}
          </div>

          {view !== 'dashboard' && (
            <div className="mx-6 my-4 overflow-hidden rounded-xl" style={{ border: `1px solid ${T.border}`, boxShadow: T.shadow, background: '#0F0F0F' }}>
              <iframe key={view + mode} title="Live sheet preview" src={`/embed/${view}/${DEMO_PID}?theme=${mode}`} style={{ width: '100%', height: 820, border: 0, display: 'block' }} />
            </div>
          )}

          {view === 'dashboard' && (
          <div className="grid grid-cols-1 gap-6 p-6 lg:grid-cols-12" style={{ maxWidth: 1700, margin: '0 auto' }}>
            {/* Active Projects w/ changeable covers */}
            <section className="lg:col-span-8">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-lg font-semibold sm:text-xl" style={{ color: T.text }}>Active Projects</h2>
                <button className="flex items-center gap-1 text-xs font-medium" style={{ color: T.gold }}>View all <ChevronRight size={13} /></button>
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                {PROJECTS.map((p, i) => (
                  <Card key={p.name} T={T} accent={p.accent} data-testid={`project-card-${i}`}>
                    <div className="group relative h-28 w-full bg-cover bg-center" style={{ backgroundImage: `url(${COVERS[i]})` }}>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100" style={{ background: 'rgba(10,10,14,0.55)' }}>
                        <button data-testid={`project-cover-change-${i}`} className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold" style={{ background: T.goldGrad, color: T.onGold }}>
                          <Camera size={13} /> Change cover
                        </button>
                      </div>
                    </div>
                    <div className="p-4">
                      <Overline T={T}>{p.rooms} Rooms</Overline>
                      <div className="mt-1 text-[15px] font-semibold" style={{ color: T.text }}>{p.name}</div>
                      <div className="text-xs" style={{ color: T.text2 }}>{p.client} · {p.addr}</div>
                      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full" style={{ background: T.cardSubtle }}>
                        <div className="h-full rounded-full" style={{ width: `${p.pct}%`, background: T[p.accent] }} />
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <Badge T={T} tone={p.accent}>In Progress</Badge>
                        <span className="text-xs font-semibold" style={{ color: T.text2 }}>{p.pct}%</span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </section>

            {/* Quick actions — OUR features */}
            <section className="lg:col-span-4">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-lg font-semibold sm:text-xl" style={{ color: T.text }}>Quick Actions</h2>
              </div>
              <Card T={T}>
                <div className="grid grid-cols-3 gap-2 p-4">
                  {ACTIONS.map((a) => (
                    <button key={a.label} data-testid={`action-${a.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                      className="flex aspect-square flex-col items-center justify-center gap-1.5 rounded-lg text-center transition-all"
                      style={{ background: T.cardSubtle, border: `1px solid ${T.border}` }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.04)'; e.currentTarget.style.borderColor = T.gold; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = T.border; }}>
                      <a.icon size={18} style={{ color: T.gold }} />
                      <span className="px-1 text-[10px] font-medium leading-tight" style={{ color: T.text2 }}>{a.label}</span>
                    </button>
                  ))}
                </div>
              </Card>
            </section>

            {/* BIG Calendar */}
            <section className="lg:col-span-8">
              <Card T={T} accent="gold">
                <div className="flex flex-wrap items-center justify-between gap-3 p-5 pb-3">
                  <div className="flex items-center gap-3">
                    <div className="grid h-9 w-9 place-items-center rounded-lg" style={{ background: T.goldGrad, color: T.onGold }}><CalIcon size={17} /></div>
                    <div>
                      <h2 className="text-lg font-semibold sm:text-xl" style={{ color: T.text }}>Project Calendar</h2>
                      <div className="text-xs" style={{ color: T.text2 }}>September 2026 · all projects</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="rounded-lg px-3 py-1.5 text-xs font-semibold" style={{ background: T.cardSubtle, color: T.text2, border: `1px solid ${T.border}` }}>Today</button>
                    <button className="grid h-8 w-8 place-items-center rounded-lg" style={{ background: T.cardSubtle, color: T.text2, border: `1px solid ${T.border}` }}><ChevronDown size={14} className="rotate-90" /></button>
                    <button className="grid h-8 w-8 place-items-center rounded-lg" style={{ background: T.cardSubtle, color: T.text2, border: `1px solid ${T.border}` }}><ChevronDown size={14} className="-rotate-90" /></button>
                    <button className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold" style={{ background: T.goldGrad, color: T.onGold }}><Plus size={13} /> Add Event</button>
                  </div>
                </div>
                <div className="px-4 pb-4">
                  <div className="grid grid-cols-7">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                      <div key={d} className="pb-2 text-center text-[11px] font-semibold uppercase tracking-wide" style={{ color: T.muted }}>{d}</div>
                    ))}
                    {Array.from({ length: 35 }).map((_, idx) => {
                      const day = idx - 1; const valid = day >= 1 && day <= 30; const today = day === 9;
                      const evs = EVENTS[day] || [];
                      return (
                        <div key={idx} className="p-1" style={{ minHeight: 96 }}>
                          <div className="flex h-full flex-col rounded-lg p-2"
                            style={{ background: valid ? T.cardSubtle : 'transparent', border: `1px solid ${valid ? T.border : 'transparent'}`, outline: today ? `2px solid ${T.gold}` : 'none' }}>
                            <div className="text-right text-[12px] font-semibold" style={{ color: today ? T.gold : (valid ? T.text2 : 'transparent') }}>{valid ? day : ''}</div>
                            <div className="mt-1 space-y-1">
                              {evs.map((e, i) => (
                                <div key={i} className="truncate rounded px-1.5 py-0.5 text-[10px] font-medium text-white" style={{ background: CATS[e.c].c }}>{e.l}</div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 px-1">
                    {Object.values(CATS).map(c => (
                      <div key={c.label} className="flex items-center gap-1.5 text-[11px]" style={{ color: T.text2 }}>
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: c.c }} />{c.label}
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </section>

            {/* BIG To-Do grouped by project */}
            <section className="lg:col-span-4">
              <Card T={T} accent="brown">
                <div className="flex items-center justify-between p-5 pb-3">
                  <div className="flex items-center gap-3">
                    <div className="grid h-9 w-9 place-items-center rounded-lg" style={{ background: T.goldGrad, color: T.onGold }}><ListTodo size={17} /></div>
                    <div>
                      <h2 className="text-lg font-semibold sm:text-xl" style={{ color: T.text }}>To-Do</h2>
                      <div className="text-xs" style={{ color: T.text2 }}>46 open across projects</div>
                    </div>
                  </div>
                  <button className="text-xs font-medium" style={{ color: T.gold }}>+ Add</button>
                </div>
                <div className="space-y-3 overflow-y-auto px-4 pb-4" style={{ maxHeight: 640 }}>
                  {TODO_GROUPS.map(g => (
                    <div key={g.name} className="overflow-hidden rounded-xl" style={{ border: `1px solid ${T.border}` }}>
                      <div className="flex items-center justify-between px-3 py-2" style={{ background: T.cardSubtle }}>
                        <div className="flex items-center gap-2 text-[13px] font-semibold" style={{ color: T.text }}>
                          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: T[g.tone] }} />{g.name}
                        </div>
                        <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: `${T[g.tone]}22`, color: T[g.tone] }}>{g.count}</span>
                      </div>
                      {g.tasks.map((t, i) => (
                        <div key={i} className="flex items-center gap-2.5 px-3 py-2.5" style={{ borderTop: `1px solid ${T.border}` }}>
                          <span className="grid h-4 w-4 shrink-0 place-items-center rounded" style={{ border: `1.5px solid ${T.borderStrong}` }} />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-[13px]" style={{ color: T.text }}>{t.t}</div>
                            {t.d && <div className="text-[10px]" style={{ color: T.muted }}>{t.d}</div>}
                          </div>
                          <span className="shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase" style={{ background: `${T[PRIO[t.p]]}22`, color: T[PRIO[t.p]] }}>{t.p}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </Card>
            </section>

            {/* Status Overview — full width */}
            <section className="lg:col-span-12">
              <Card T={T} accent="gold">
                <div className="grid grid-cols-1 gap-6 p-5 lg:grid-cols-12">
                  <div className="lg:col-span-4">
                    <h2 className="text-lg font-semibold sm:text-xl" style={{ color: T.text }}>Status Overview</h2>
                    <div className="text-xs" style={{ color: T.text2 }}>{total} items · Diehl Lakehouse</div>
                    <div className="mt-4 flex items-center gap-4">
                      <div className="relative h-28 w-28 shrink-0 rounded-full" style={{ background: `conic-gradient(${stops})` }}>
                        <div className="absolute inset-[12px] grid place-items-center rounded-full" style={{ background: T.card }}>
                          <div className="text-center">
                            <div className="text-xl font-bold leading-none" style={{ color: T.text }}>{total}</div>
                            <div className="text-[9px]" style={{ color: T.muted }}>ITEMS</div>
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 space-y-1.5">
                        {STATUS.slice(0, 4).map(s => (
                          <div key={s.label} className="flex items-center gap-2 text-xs">
                            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: T[s.tone] }} />
                            <span className="flex-1 truncate" style={{ color: T.text2 }}>{s.label}</span>
                            <span className="font-semibold" style={{ color: T.text }}>{s.n}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-x-8 gap-y-2.5 sm:grid-cols-2 lg:col-span-8">
                    {STATUS.map(s => (
                      <div key={s.label}>
                        <div className="mb-0.5 flex items-center justify-between text-[12px]">
                          <span style={{ color: T.text2 }}>{s.label}</span><span className="font-semibold" style={{ color: T.text }}>{s.n}</span>
                        </div>
                        <div className="h-2 w-full overflow-hidden rounded-full" style={{ background: T.cardSubtle }}>
                          <div className="h-full rounded-full" style={{ width: `${(s.n / 184) * 100}%`, background: T[s.tone] }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </section>
          </div>
          )}

          <div className="pb-10 text-center text-xs" style={{ color: T.muted }}>
            Concept v3 · gold on deep navy→black · {mode === 'dark' ? 'Dark' : 'Light'} mode ·
            <span style={{ color: T.gold }}> FF&E, Checklist, Walkthrough & Trade sheets stay exactly as they are</span>
          </div>
        </main>
      </div>
      <CommandPalette open={cmd} onClose={() => setCmd(false)} T={T} />
    </div>
  );
};

export default LayoutOptionsPreview;
